// Hook React enveloppant Web Speech API pour la dictée vocale (P3 chantier DAL).
// Cible : Chrome Android, Safari iOS 14.5+, Edge desktop. Firefox ne supporte pas
// SpeechRecognition (le bouton micro reste caché via `supported=false`).
//
// Usage :
//   const { supported, listening, transcript, start, stop, reset } = useSpeechRecognition({ lang: "fr-FR" });
//
// Le hook accumule les `final results` dans `transcript`. L'appelant lit la valeur
// au stop pour merger avec son state local (cf. mergeTranscript dans speechHelpers).

import { useCallback, useEffect, useRef, useState } from "react";
import { isSpeechSupported, poserFinal } from "../components/dal/speechHelpers";

export default function useSpeechRecognition({ lang = "fr-FR", continuous = true } = {}) {
  // 🔴 N°634 — DÉTECTION SYNCHRONE, dès le PREMIER rendu.
  // ⚠️ `useState(false)` + effet faisait apparaître le badge « indisponible »
  // à CHAQUE premier paint, avant de le remplacer par le bouton. Un clignotement
  // ⛔ n'est pas un état : quelqu'un qui regarde au mauvais instant lit
  // « dictée indisponible » alors qu'elle l'est.
  const [supported, setSupported] = useState(
    () => typeof window !== "undefined" && isSpeechSupported(window));
  // 🔑 POURQUOI elle est indisponible — ⛔ un écran qui dit la MÊME chose pour
  // des causes DIFFÉRENTES ⛔ ne permet à personne de trancher à distance.
  const [motifIndispo, setMotifIndispo] = useState(null);
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  const [error, setError] = useState(null);
  const recognitionRef = useRef(null);
  // 🔑 REF, ⛔ pas une variable d'effet : `start()` vit HORS de l'effet et doit
  // pouvoir VIDER les résultats. Sans ça, une seconde dictée repartirait avec
  // les segments de la première — la duplication, par la porte de derrière.
  const finalsRef = useRef([]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!isSpeechSupported(window)) {
      setSupported(false);
      setMotifIndispo({ cause: "objet_absent",
                        detail: "ce navigateur n'expose ni SpeechRecognition "
                                + "ni webkitSpeechRecognition" });
      return;
    }
    // 🔴 N°634 — LA CONSTRUCTION EST GARDÉE. ⚠️ MESURÉ par INTÉGRATION : si
    // `new Ctor()` (ou la configuration qui suit) LÈVE, l'exception part dans
    // le rendu et c'est TOUT L'ÉCRAN DE VISITE qui disparaît — ⛔ pas seulement
    // le micro. La promesse du N°596 (« une fonction essentielle ⛔ ne disparaît
    // pas ») ⛔ ne tenait PAS dans ce cas : il n'y avait ⛔ AUCUN try/catch.
    let rec;
    try {
      const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition;
      rec = new Ctor();
      rec.continuous = continuous;
      rec.interimResults = true;
      rec.lang = lang;
    } catch (e) {
      setSupported(false);
      setMotifIndispo({ cause: "constructeur_leve",
                        detail: `${e && e.name ? e.name : "Erreur"} : `
                                + `${(e && e.message) || "construction refusée"}` });
      return;                      // ⛔ l'écran RESTE, la saisie clavier aussi
    }

    // 🔴 LES RÉSULTATS SONT INDEXÉS, ⛔ PAS EMPILÉS.
    //
    // ⚠️ CE QUE FAISAIT L'ANCIEN CODE : `setTranscript(prev => prev + finalText)`
    // — un APPEND aveugle. Or la Web Speech API fait DEUX choses parfaitement
    // normales que l'append ⛔ ne supporte pas :
    //   · elle RÉ-ÉMET des résultats déjà finaux (`resultIndex` revient en
    //     arrière en mode continu) ⇒ le texte était AJOUTÉ UNE SECONDE FOIS
    //     ⇒ « mots répétés » ;
    //   · elle RÉVISE un résultat final après coup (« sintofer » corrigé)
    //     ⇒ l'ancien ET le nouveau se retrouvaient à la suite.
    // Et en aval, `extractTranscriptDelta` découpait par LONGUEUR : sur un
    // transcript réécrit, la coupe tombait AU MILIEU D'UN MOT ⇒ « exterieure-
    // protection », « polyanebrossage ». Les deux symptômes de JJ, une cause.
    // 🔑 ⇒ On INDEXE : `finals[i]` est ÉCRASÉ, jamais empilé. Ré-émettre ou
    // réviser le résultat i redonne exactement le même texte — l'opération est
    // IDEMPOTENTE, donc ⛔ ni duplication ⛔ ni mot coupé, par construction.
    const finals = finalsRef.current;
    rec.onresult = (event) => {
      let interimText = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const res = event.results[i];
        // N°591 — FINAL AJOUTÉ UNE SEULE FOIS, desktop ET Android (cf. poserFinal).
        // L'interim, lui, ⛔ n'entre jamais dans le champ : il est REMPLACÉ à
        // chaque événement et seulement affiché à côté du micro.
        if (res.isFinal) poserFinal(finals, i, res[0].transcript);
        else interimText += res[0].transcript;
      }
      setTranscript(finals.filter(Boolean).join(" "));
      setInterimTranscript(interimText);
    };
    rec.onerror = (e) => {
      setError(e.error || "speech-recognition-error");
      setListening(false);
    };
    rec.onend = () => {
      setListening(false);
      setInterimTranscript("");
    };

    recognitionRef.current = rec;
    setSupported(true);
    setMotifIndispo(null);
    return () => {
      try { rec.stop(); } catch {}
      recognitionRef.current = null;
    };
  }, [lang, continuous]);

  const start = useCallback(() => {
    if (!recognitionRef.current || listening) return;
    setError(null);
    finalsRef.current.length = 0;
    setTranscript("");
    setInterimTranscript("");
    try {
      recognitionRef.current.start();
      setListening(true);
    } catch (e) {
      // start() peut throw si déjà actif
      setError(e.message || String(e));
    }
  }, [listening]);

  const stop = useCallback(() => {
    if (!recognitionRef.current || !listening) return;
    try { recognitionRef.current.stop(); } catch {}
  }, [listening]);

  const reset = useCallback(() => {
    setTranscript("");
    setInterimTranscript("");
    setError(null);
  }, []);

  return { supported, motifIndispo, listening, transcript, interimTranscript,
           error, start, stop, reset };
}
